import { useMemo, useState, ReactElement, useCallback, useRef } from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import cn from 'classnames'

import {
  PageLayout,
  Button,
  DropdownButton,
  useGoogleMaps,
  mapMode as mapModes,
  safetyCaseValuesToSafetyCaseUpdateUpsert,
  safetyCaseToSafetyCaseValues,
  showSuccessAlert,
  showErrorAlert,
  ISafetyCaseValues,
  ISafetyCaseUpsertValues,
} from '../../../components'
import SafetyCaseBuilder from './safety-case-builder'

import {
  useSafetyCaseByIdQuery,
  useUpdateSafetyCaseMutation,
  useUpdateSafetyReportInternalAnswerMutation,
} from '../../../services'
import { useAppContext } from '../../../app-context'
import { isDeepEqual } from '../../../util'
import { claims, useAccessControl } from '../../../security'

import { IGeoJson } from '../../../interfaces'
import { useBlocker } from '../../../hooks'

const UpdateSafetyCasePage = () => {
  const navigate = useNavigate()

  const { hasAccess } = useAccessControl()

  const hasSafetyCaseWriteAccess = useMemo(() => hasAccess([claims.safetyCase.write]), [hasAccess])

  const { dispatch: appDispatch } = useAppContext()

  const { safetyCaseId } = useParams()

  const { setMapMode } = useGoogleMaps()

  const [safetyCaseGeoJson, setSafetyCaseGeoJson] = useState<IGeoJson | undefined>()

  const safetyCaseQuery = useSafetyCaseByIdQuery(
    { id: Number(safetyCaseId) },
    {
      enabled: safetyCaseId !== undefined && safetyCaseId !== null,
      onSuccess: safetyCase => {
        setMapMode(mapModes.CLEAR)
        setSafetyCaseGeoJson(safetyCase?.geoJson)
      },
    },
  )

  const { data: safetyCase, refetch: refetchSafetyCase, isFetching: isSafetyCaseFetching } = safetyCaseQuery

  const location = useLocation()

  const [headerControls, setHeaderControls] = useState<ReactElement | undefined>(undefined)

  const [backSearch] = useState((location?.state as any)?.backSearch)

  const _headerControls = useMemo(
    () => (
      <div>
        <Button
          onClick={() => navigate({ pathname: '/safety-cases/safety-case-list', search: backSearch })}
          className={cn(hasSafetyCaseWriteAccess && 'mr-2')}
          type="secondary"
        >
          Back To Case List
        </Button>

        {hasSafetyCaseWriteAccess && headerControls}
      </div>
    ),
    [hasSafetyCaseWriteAccess, headerControls, navigate, backSearch],
  )

  const { mutateAsync: updateSafetyCase, isLoading: isSafetyCaseUpdating } = useUpdateSafetyCaseMutation({})

  const { mutateAsync: updateSafetyReportInternalAnswer } = useUpdateSafetyReportInternalAnswerMutation({})

  const isSafetyCaseCompleted = safetyCase?.completed

  const isSaveDisabled = useMemo(
    () => isSafetyCaseFetching || isSafetyCaseUpdating,
    [isSafetyCaseFetching, isSafetyCaseUpdating],
  )

  const saveSafetyCaseInitialValues = useMemo(() => safetyCaseToSafetyCaseValues(safetyCase), [safetyCase])

  const [saveSafetyCaseValues, setSaveSafetyCaseValues] = useState<ISafetyCaseUpsertValues | undefined>(
    saveSafetyCaseInitialValues,
  )

  const isSafetyCaseChanged = useMemo(() => {
    // Ensure tagIds arrays are sorted before comparison
    const sortedInitialValues =
      saveSafetyCaseInitialValues && saveSafetyCaseInitialValues.tagIds
        ? {
            ...saveSafetyCaseInitialValues,
            tagIds: [...saveSafetyCaseInitialValues.tagIds].sort((a, b) => a - b),
          }
        : saveSafetyCaseInitialValues
    const sortedValues =
      saveSafetyCaseValues && saveSafetyCaseValues.tagIds
        ? { ...saveSafetyCaseValues, tagIds: [...saveSafetyCaseValues.tagIds].sort((a, b) => a - b) }
        : saveSafetyCaseValues

    return (
      !isSafetyCaseFetching &&
      ((sortedValues ? !isDeepEqual(sortedInitialValues, sortedValues) : false) ||
        !isDeepEqual(safetyCaseGeoJson, safetyCase?.geoJson || undefined))
    )
  }, [saveSafetyCaseValues, saveSafetyCaseInitialValues, safetyCaseGeoJson, safetyCase, isSafetyCaseFetching])

  const isSaveButtonDisabled = useMemo(
    () => isSaveDisabled || !isSafetyCaseChanged,
    [isSaveDisabled, isSafetyCaseChanged],
  )

  const [saveSafetyCaseForm, setSaveSafetyCaseForm] = useState<any>()

  const isUpdateAndCompleteRef = useRef<boolean>()

  const handleUpdateSafetyCase = useCallback(
    async (safetyCaseValues: ISafetyCaseValues) => {
      if (!safetyCase) {
        return
      }

      let safetyCaseUpdateUpsert = safetyCaseValuesToSafetyCaseUpdateUpsert(safetyCaseValues, {
        safetyCase,
        geoJson: safetyCaseGeoJson,
      })

      if (!safetyCaseUpdateUpsert) {
        return
      }

      try {
        if (isUpdateAndCompleteRef.current) {
          safetyCaseUpdateUpsert.completed = true
        }

        await updateSafetyCase(safetyCaseUpdateUpsert)
      } catch (err) {
        appDispatch(showErrorAlert('Error updating safety case'))
        console.warn(err)
        return
      }

      let successMessage = 'Safety case successfully updated'
      if (isUpdateAndCompleteRef.current) {
        successMessage = 'Safety case successfully updated and completed'
      }

      setMapMode(mapModes.CLEAR)
      appDispatch(showSuccessAlert(successMessage))
      refetchSafetyCase()
    },
    [appDispatch, safetyCase, updateSafetyCase, refetchSafetyCase, safetyCaseGeoJson, setMapMode],
  )

  const handleUpdateSafetyReportInternalAnswer = useCallback(
    async (payload: { id: number; internalAnswer: string }) => {
      try {
        await updateSafetyReportInternalAnswer(payload)
        appDispatch(showSuccessAlert('Safety case report answer successfully updated'))
        refetchSafetyCase()
      } catch (err) {
        appDispatch(showErrorAlert('Error while updating safety case report answer'))
        console.warn('Error while updating safety case report answer', err)
      }
    },
    [appDispatch, updateSafetyReportInternalAnswer, refetchSafetyCase],
  )

  // Prompt user if they try to leave the page with unsaved changes
  useBlocker(
    useCallback(
      tx => {
        if (isSafetyCaseChanged && hasSafetyCaseWriteAccess) {
          const confirmLeave = window.confirm('You have unsaved changes. Are you sure you want to leave this page?')
          if (confirmLeave) {
            // Reset the form values to initial values if the user confirms leaving
            setSaveSafetyCaseValues(saveSafetyCaseInitialValues)
            tx.retry()
          }
        } else {
          tx.retry()
        }
      },
      [isSafetyCaseChanged, saveSafetyCaseInitialValues, hasSafetyCaseWriteAccess],
    ),
  )

  const saveHeaderControls = useMemo(() => {
    if (isSafetyCaseCompleted) {
      return (
        <Button
          disabled={isSaveButtonDisabled}
          onClick={() => {
            isUpdateAndCompleteRef.current = false
            saveSafetyCaseForm?.submit()
          }}
        >
          Save
        </Button>
      )
    }

    return (
      <DropdownButton
        className="ml-2"
        disabled={isSaveButtonDisabled && isSaveDisabled}
        iconClassName="bi-chevron-down ml-2"
        type="primary"
        options={[
          {
            label: 'Save',
            disabled: isSaveButtonDisabled,
            handler: () => {
              isUpdateAndCompleteRef.current = false
              saveSafetyCaseForm?.submit()
            },
          },
          {
            label: isSafetyCaseChanged ? 'Save & Complete' : 'Complete',
            disabled: isSaveDisabled,
            handler: () => {
              isUpdateAndCompleteRef.current = true
              saveSafetyCaseForm?.submit()
            },
          },
        ]}
      >
        Save
      </DropdownButton>
    )
  }, [isSaveButtonDisabled, isSaveDisabled, isSafetyCaseChanged, saveSafetyCaseForm, isSafetyCaseCompleted])

  const pageTitle = useMemo(
    () => `Safety Case ${safetyCaseQuery?.data?.name ? `- ${safetyCaseQuery?.data?.name}` : ''}`,
    [safetyCaseQuery?.data?.name],
  )

  return (
    <PageLayout title={pageTitle} headerControls={_headerControls}>
      <SafetyCaseBuilder
        hasWriteAccess={hasSafetyCaseWriteAccess}
        safetyCaseQuery={safetyCaseQuery}
        setHeaderControls={setHeaderControls}
        handleSaveSafetyCase={handleUpdateSafetyCase}
        handleUpdateSafetyReportInternalAnswer={handleUpdateSafetyReportInternalAnswer}
        saveHeaderControls={saveHeaderControls}
        setSaveSafetyCaseForm={setSaveSafetyCaseForm}
        saveSafetyCaseInitialValues={saveSafetyCaseInitialValues}
        setSaveSafetyCaseValues={setSaveSafetyCaseValues}
        isSaveFormDisabled={isSaveDisabled}
      />
    </PageLayout>
  )
}

export default UpdateSafetyCasePage
